// src/pages/Clients/Detail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Grid,
  Chip,
  Alert,
  Avatar,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AttachMoney as FinancialIcon,
  Description as DocumentIcon,
  LocalHospital as MedicalIcon,
  Info as OverviewIcon,
  MoreVert as MoreVertIcon,
  BookOnline as BookIcon
} from '@mui/icons-material';
import {
  Person as PersonIcon,
  Tune as TuneIcon,
  Star as StarIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import { useClient } from '../../hooks/useClients';
import clientsApi from '../../api/clientsApi';
import DevOnly from '../../components/DevOnly';
import ClientAlerts from './Alerts';
import ClientAllergies from './Allergies';
import ClientCommunications from './Communications';
import ClientDocuments from './Documents';
import ClientForms from './Forms';
import ClientInvoices from './Invoices';
import ClientNotes from './Notes';
import ClientPayments from './Payments';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientDetail = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const { data: client, isLoading, error } = useClient(id);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const fetchCounts = async () => {
      if (!id) return;
      try {
        const countsData = await clientsApi.getClientCounts(id);
        setCounts(countsData);
      } catch (err) {
        console.error('Error fetching client counts:', err);
        setCounts({
          alerts: 0,
          allergies: 0,
          communications: 0,
          documents: 0,
          notes: 0,
          payments: 0,
          invoices: 0,
          forms: 0
        });
      }
    };

    fetchCounts();
  }, [id]);

  const tabs = [
    { label: 'Overview', icon: <OverviewIcon />, id: 'overview' },
    { label: 'Forms', icon: <DocumentIcon />, id: 'forms' },
    { label: 'Allergies', icon: <MedicalIcon />, id: 'allergies' },
    { label: 'Notes', icon: <DocumentIcon />, id: 'notes' },
    { label: 'Alerts', icon: <MedicalIcon />, id: 'alerts' },
    { label: 'Documents', icon: <DocumentIcon />, id: 'documents' },
    { label: 'Communications', icon: <EmailIcon />, id: 'communications' },
    { label: 'Invoices', icon: <FinancialIcon />, id: 'invoices' },
    { label: 'Payments', icon: <FinancialIcon />, id: 'payments' }
  ];

  const quickStats = [
    {
      key: 'forms',
      label: 'Forms',
      description: 'Completed or pending paperwork',
      icon: DocumentIcon,
      accent: 'primary'
    },
    {
      key: 'alerts',
      label: 'Alerts',
      description: 'Clinical or operational warnings',
      icon: MedicalIcon,
      accent: 'error'
    },
    {
      key: 'communications',
      label: 'Communications',
      description: 'Emails, calls, and messages',
      icon: EmailIcon,
      accent: 'secondary'
    },
    {
      key: 'invoices',
      label: 'Invoices',
      description: 'Outstanding and paid billing',
      icon: FinancialIcon,
      accent: 'success'
    },
    {
      key: 'payments',
      label: 'Payments',
      description: 'Recent payment activity',
      icon: FinancialIcon,
      accent: 'info'
    }
  ];

  const heroSurface = alpha(theme.palette.primary.main, 0.05);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBack = () => {
    navigate('/clients');
  };

  const handleEdit = () => {
    navigate(`/clients/${id}/edit`);
  };

  const handleBookAppointment = () => {
    navigate(`/calendar/book?clientId=${id}`);
  };

  const handleSendMessage = () => {
    navigate(`/communications/new?clientId=${id}`);
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Clients
        </Button>
        <Alert severity="error">Failed to load client details</Alert>
      </Box>
    );
  }

  if (!client) {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Clients
        </Button>
        <Alert severity="warning">Client not found</Alert>
      </Box>
    );
  }

  const clientName = [client.salutation, client.first_name, client.last_name]
    .filter(Boolean)
    .join(' ') || client.full_name;

  const lastVisitLabel = client.last_visit_date
    ? `Last visit ${formatDistanceToNow(new Date(client.last_visit_date), { addSuffix: true })}`
    : 'Never visited';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        variant="outlined"
        size="small"
        sx={{ mb: 3, borderRadius: 999 }}
      >
        Back to Clients
      </Button>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: heroSurface,
          color: theme.palette.text.primary,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.1)',
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.12),
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 4,
            borderRadius: '4px 0 0 4px',
            backgroundColor: alpha(theme.palette.primary.main, 0.6)
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <Avatar
              sx={{
                width: 96,
                height: 96,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                border: `3px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                boxShadow: '0 16px 38px rgba(15,23,42,0.12)',
                fontSize: '2rem'
              }}
              src={client.avatar_url}
            >
              {getInitials(client.first_name, client.last_name)}
            </Avatar>
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {clientName}
                </Typography>
                <Chip
                  label={client.is_active ? 'Active' : 'Inactive'}
                  sx={{
                    borderRadius: 999,
                    borderColor: client.is_active
                      ? alpha(theme.palette.success.main, 0.4)
                      : alpha(theme.palette.grey[500], 0.4),
                    color: client.is_active
                      ? theme.palette.success.dark
                      : theme.palette.text.secondary,
                    bgcolor: client.is_active
                      ? alpha(theme.palette.success.main, 0.12)
                      : alpha(theme.palette.grey[500], 0.12),
                    borderRadius: 999,
                    fontWeight: 600
                  }}
                />
                {client.labels?.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    sx={{
                      borderRadius: 999,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main
                    }}
                  />
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <Chip
                  icon={<EmailIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />}
                  label={client.email || 'No email'}
                  variant="outlined"
                  sx={{
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                    color: theme.palette.primary.main,
                    borderRadius: 999
                  }}
                />
                <Chip
                  icon={<PhoneIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />}
                  label={client.mobile || client.phone || 'No phone'}
                  variant="outlined"
                  sx={{
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                    color: theme.palette.primary.main,
                    borderRadius: 999
                  }}
                />
                <Chip
                  label={lastVisitLabel}
                  variant="outlined"
                  sx={{
                    borderColor: alpha(theme.palette.text.primary, 0.2),
                    color: alpha(theme.palette.text.primary, 0.8),
                    borderRadius: 999
                  }}
                />
                {client.pabau_score && (
                  <Chip
                    label={`Score ${client.pabau_score}/100`}
                    sx={{
                      borderRadius: 999,
                      bgcolor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.dark
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <DevOnly>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  boxShadow: 'none'
                }}
              >
                Edit Profile
              </Button>
            </DevOnly>
            <DevOnly>
              <Button
                variant="outlined"
                startIcon={<BookIcon />}
                onClick={handleBookAppointment}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  borderColor: alpha(theme.palette.primary.main, 0.3)
                }}
              >
                Book Appointment
              </Button>
            </DevOnly>
            <DevOnly>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={handleSendMessage}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  borderColor: alpha(theme.palette.primary.main, 0.3)
                }}
              >
                Send Message
              </Button>
            </DevOnly>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                color: theme.palette.text.secondary,
                border: '1px solid',
                borderColor: alpha(theme.palette.text.primary, 0.18),
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.85)
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {quickStats.map(({ key, label, description, icon: StatIcon, accent }) => {
          const total = counts?.[key] ?? 0;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
              <Card
                elevation={0}
                sx={(theme) => {
                  const accentColor = theme.palette?.[accent]?.main || theme.palette.primary.main;
                  return {
                    borderRadius: 3,
                    height: '100%',
                    border: '1px solid',
                    borderColor: alpha(accentColor, 0.18),
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.08)'
                  };
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box
                        sx={(theme) => {
                          const accentColor = theme.palette?.[accent]?.main || theme.palette.primary.main;
                          return {
                            width: 44,
                            height: 44,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            bgcolor: alpha(accentColor, 0.15),
                            color: accentColor
                          };
                        }}
                      >
                        <StatIcon fontSize="medium" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', maxWidth: 150 }}>
                        {description}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                        {label}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.text.primary
                        }}
                      >
                        {total}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.9),
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 20px 44px rgba(15, 23, 42, 0.08)'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            px: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            '& .MuiTab-root': {
              minWidth: 'auto',
              fontSize: '0.9rem',
              px: 1.5,
              py: 1,
              textTransform: 'none',
              minHeight: 48,
              borderRadius: 999,
              mr: 0.75,
              color: 'text.secondary',
              transition: 'all 160ms ease',
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                boxShadow: '0 12px 28px rgba(99, 102, 241, 0.18)'
              }
            }
          }}
        >
          {tabs.map((tab, index) => {
            const countMap = {
              alerts: counts.alerts,
              documents: counts.documents,
              communications: counts.communications,
              invoices: counts.invoices,
              payments: counts.payments,
              forms: counts.forms,
              notes: counts.notes,
              allergies: counts.allergies
            };
            const count = countMap[tab.id];
            return (
              <Tab
                key={tab.id}
                label={count !== undefined ? `${tab.label} (${count ?? 0})` : tab.label}
                icon={tab.icon}
                iconPosition="start"
                id={`client-tab-${index}`}
                aria-controls={`client-tabpanel-${index}`}
              />
            );
          })}
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <ClientOverviewTab client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ClientForms clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <ClientAllergies clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <ClientNotes clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <ClientAlerts clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <ClientDocuments clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={6}>
          <ClientCommunications clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={7}>
          <ClientInvoices clientId={id} client={client} />
        </TabPanel>
        <TabPanel value={activeTab} index={8}>
          <ClientPayments clientId={id} client={client} />
        </TabPanel>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <DevOnly>
          <MenuItem onClick={() => { handleMenuClose(); }}>
            {client.is_active ? 'Deactivate Client' : 'Activate Client'}
          </MenuItem>
        </DevOnly>
        <MenuItem onClick={() => { handleMenuClose(); }}>
          Export Client Data
        </MenuItem>
        <DevOnly>
          <MenuItem onClick={() => { handleMenuClose(); }} sx={{ color: 'error.main' }}>
            Delete Client
          </MenuItem>
        </DevOnly>
      </Menu>
    </Box>
  );
};

const ClientOverviewTab = ({ client }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={4}>
      <SectionCard title="Demographics" icon={<PersonIcon fontSize="small" />} tone="secondary">
        <List dense sx={{ '& .MuiListItem-root': { px: 0, py: 1.2, borderBottom: '1px dashed', borderColor: 'divider' } }}>
          <ListItem>
            <ListItemText
              primary="Date of Birth"
              secondary={client.date_of_birth ? new Date(client.date_of_birth).toLocaleDateString() : 'Not provided'}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Gender"
              secondary={client.gender || 'Not specified'}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Address"
              secondary={client.street_address || 'Not provided'}
            />
          </ListItem>
        </List>
      </SectionCard>
    </Grid>

    <Grid item xs={12} md={4}>
      <SectionCard title="Preferences" icon={<TuneIcon fontSize="small" />} tone="primary">
        <List dense sx={{ '& .MuiListItem-root': { px: 0, py: 1.2, borderBottom: '1px dashed', borderColor: 'divider' } }}>
          <ListItem>
            <ListItemText
              primary="Email Marketing"
              secondary={client.email_opt_in ? 'Opted In' : 'Opted Out'}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="SMS Marketing"
              secondary={client.sms_opt_in ? 'Opted In' : 'Opted Out'}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Communication Preference"
              secondary={client.preferred_contact_method || 'Email'}
            />
          </ListItem>
        </List>
      </SectionCard>
    </Grid>

    <Grid item xs={12} md={4}>
      <SectionCard title="Client Score" icon={<StarIcon fontSize="small" />} tone="warning">
        {client.pabau_score ? (
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.dark' }}>
              {client.pabau_score}/100
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {client.pabau_score >= 80 ? 'High Value Client' :
               client.pabau_score >= 60 ? 'Regular Client' : 'New Client'}
            </Typography>
          </Box>
        ) : (
          <Typography color="text.secondary">No score available</Typography>
        )}
      </SectionCard>
    </Grid>

    <Grid item xs={12}>
      <SectionCard title="Recent Activity Timeline" icon={<TimelineIcon fontSize="small" />} tone="info">
        <Typography color="text.secondary">
          Activity timeline will show recent appointments, payments, and communications
        </Typography>
      </SectionCard>
    </Grid>
  </Grid>
);

const SectionCard = ({ title, icon, children, tone = 'primary' }) => {
  const theme = useTheme();
  const accent = theme.palette?.[tone]?.main || theme.palette.primary.main;
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(accent, 0.18),
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 16px 32px rgba(15, 23, 42, 0.08)'
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accent, 0.18),
              color: accent
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
};

export default ClientDetail;
